/**
 * Presentation Mode — Full-screen 3D viewer with overlay, auto-play, video recording, and CSV export.
 */
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  X, Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight,
  Camera, Download, Calendar, Clock, Activity, CheckCircle2, AlertTriangle, Layers,
  Video, Square, FileDown
} from 'lucide-react';

export function PresentationMode({
  onClose, snapshots, currentIndex, onIndexChange,
  isPlaying, onTogglePlay, playSpeed, onSpeedChange,
  currentSnapshot, vizMode, children
}) {
  const [csvRecording, setCsvRecording] = useState(false);
  const [recordedFrames, setRecordedFrames] = useState([]);
  const [videoRecording, setVideoRecording] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoBlobRef = useRef(null);
  const viewerAreaRef = useRef(null);

  const total = snapshots?.length || 0;
  const metrics = currentSnapshot?.metrics || {};
  const planned = metrics.plannedProgressPct || 0;
  const actual = metrics.actualProgressPct || 0;
  const delta = actual - planned;
  const periodLabel = currentSnapshot?.periodLabel || snapshots?.[currentIndex]?.periodLabel || '';

  // CSV record mode: capture frame info at each snapshot step
  useEffect(() => {
    if (!csvRecording || !isPlaying) return;
    setRecordedFrames(prev => [...prev, {
      step: currentIndex,
      period: periodLabel,
      planned: planned.toFixed(1),
      actual: actual.toFixed(1),
      visible: metrics.objectsVisible || 0,
      inProgress: metrics.activitiesInProgress || 0,
      completed: metrics.activitiesCompleted || 0,
    }]);
  }, [currentIndex, csvRecording, isPlaying]);

  const handleStartCsvRecording = () => {
    setRecordedFrames([]);
    setCsvRecording(true);
    if (!isPlaying) onTogglePlay();
  };

  const handleStopCsvRecording = () => {
    setCsvRecording(false);
    if (isPlaying) onTogglePlay();
  };

  const handleExportCsv = () => {
    if (recordedFrames.length === 0) return;
    const csv = [
      'Step,Period,Planned%,Actual%,ObjectsVisible,InProgress,Completed',
      ...recordedFrames.map(f => `${f.step},${f.period},${f.planned},${f.actual},${f.visible},${f.inProgress},${f.completed}`)
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `4D_Presentation_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Video recording via MediaRecorder on canvas
  const handleStartVideoRecording = () => {
    const area = viewerAreaRef.current;
    if (!area) return;
    // Find the canvas element inside the viewer
    const canvas = area.querySelector('canvas');
    if (!canvas) {
      // Fallback: capture the entire viewer area as a video
      try {
        const stream = canvas ? canvas.captureStream(30) : null;
        if (!stream) {
          alert('No canvas found in the viewer. Video recording requires a real 3D model loaded.');
          return;
        }
      } catch {
        alert('Video recording not supported in this browser or viewer mode.');
        return;
      }
    }

    try {
      const stream = canvas.captureStream(30);
      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm',
      });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        videoBlobRef.current = blob;
        setVideoReady(true);
        setVideoRecording(false);
      };
      recorder.start(100); // collect data every 100ms
      mediaRecorderRef.current = recorder;
      setVideoRecording(true);
      setVideoReady(false);
      videoBlobRef.current = null;
      // Auto-play if not playing
      if (!isPlaying) onTogglePlay();
    } catch (err) {
      console.error('Video recording error:', err);
      alert('Failed to start video recording. Ensure a real 3D model is loaded.');
    }
  };

  const handleStopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (isPlaying) onTogglePlay();
  };

  const handleDownloadVideo = () => {
    if (!videoBlobRef.current) return;
    const url = URL.createObjectURL(videoBlobRef.current);
    const a = document.createElement('a');
    a.href = url;
    a.download = `4D_Presentation_${new Date().toISOString().slice(0, 10)}.webm`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stop video recording when playback ends
  useEffect(() => {
    if (videoRecording && !isPlaying && mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // Small delay to capture the last frame
      setTimeout(() => handleStopVideoRecording(), 500);
    }
  }, [isPlaying, videoRecording]);

  // ESC to exit
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="presentation-mode">
      {/* 3D Viewer fills the screen */}
      <div className="flex-1 relative" ref={viewerAreaRef}>
        {children}
        {videoRecording && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold z-20 animate-pulse" data-testid="video-recording-indicator">
            <div className="w-2 h-2 bg-white rounded-full" />
            REC
          </div>
        )}
      </div>

      {/* Bottom control bar */}
      <div className="bg-black/80 backdrop-blur-xl border-t border-white/10 px-6 py-3">
        <div className="flex items-center gap-4">
          {/* Left: period + progress */}
          <div className="flex items-center gap-3 min-w-[220px]">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-white font-semibold text-sm">{periodLabel}</span>
            <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-[10px]">{planned.toFixed(1)}% Plan</Badge>
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">{actual.toFixed(1)}% Act</Badge>
            <span className={`text-xs font-mono ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
            </span>
          </div>

          {/* Center: playback */}
          <div className="flex-1 flex items-center justify-center gap-2">
            <NavBtn icon={SkipBack} onClick={() => onIndexChange(0)} />
            <NavBtn icon={ChevronLeft} onClick={() => onIndexChange(Math.max(0, currentIndex - 1))} />
            <button
              onClick={onTogglePlay}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg ${
                isPlaying
                  ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30'
                  : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-500/30'
              }`}
              data-testid="presentation-play-btn"
            >
              {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </button>
            <NavBtn icon={ChevronRight} onClick={() => onIndexChange(Math.min(total - 1, currentIndex + 1))} />
            <NavBtn icon={SkipForward} onClick={() => onIndexChange(total - 1)} />

            {/* Scrubber */}
            <div className="flex-1 max-w-md mx-3 relative group">
              <input
                type="range" min={0} max={total - 1} value={currentIndex}
                onChange={e => onIndexChange(Number(e.target.value))}
                className="w-full h-1.5 appearance-none bg-white/15 rounded-full cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-400
                  [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-blue-400/50"
                data-testid="presentation-scrubber"
              />
              <div
                className="absolute top-0 left-0 h-1.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full pointer-events-none"
                style={{ width: `${(currentIndex / Math.max(total - 1, 1)) * 100}%` }}
              />
            </div>

            {/* Speed */}
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-white/40" />
              <Select value={String(playSpeed)} onValueChange={v => onSpeedChange(Number(v))}>
                <SelectTrigger className="h-7 w-16 text-xs bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0.25, 0.5, 1, 2, 4].map(s => <SelectItem key={s} value={String(s)}>{s}x</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Right: stats + recording controls + close */}
          <div className="flex items-center gap-2 min-w-[340px] justify-end">
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-white/40"><Layers className="w-3 h-3 inline mr-0.5" />{metrics.objectsVisible || 0}</span>
              <span className="text-blue-400"><Activity className="w-3 h-3 inline mr-0.5" />{metrics.activitiesInProgress || 0}</span>
              <span className="text-emerald-400"><CheckCircle2 className="w-3 h-3 inline mr-0.5" />{metrics.activitiesCompleted || 0}</span>
            </div>

            <div className="w-px h-5 bg-white/10" />

            {/* Video recording controls */}
            {videoRecording ? (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-300 hover:text-red-200 gap-1" onClick={handleStopVideoRecording} data-testid="stop-video-btn">
                <Square className="w-3 h-3 fill-current" />Stop Video
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-white/60 hover:text-white gap-1" onClick={handleStartVideoRecording} data-testid="start-video-btn" disabled={csvRecording}>
                <Video className="w-3.5 h-3.5" />Record Video
              </Button>
            )}
            {videoReady && !videoRecording && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-400 hover:text-emerald-300 gap-1" onClick={handleDownloadVideo} data-testid="download-video-btn">
                <Download className="w-3 h-3" />Download .webm
              </Button>
            )}

            <div className="w-px h-5 bg-white/10" />

            {/* CSV recording controls */}
            {csvRecording ? (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                <span className="text-amber-400 text-xs font-mono">{recordedFrames.length}f</span>
                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-amber-300 hover:text-amber-200 px-1.5" onClick={handleStopCsvRecording} data-testid="stop-record-btn">
                  Stop
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-white/40 hover:text-white gap-1" onClick={handleStartCsvRecording} data-testid="start-record-btn" disabled={videoRecording}>
                <Camera className="w-3 h-3" />CSV
              </Button>
            )}
            {recordedFrames.length > 0 && !csvRecording && (
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-white/40 hover:text-white gap-1 px-1.5" onClick={handleExportCsv} data-testid="export-record-btn">
                <FileDown className="w-3 h-3" />{recordedFrames.length}f
              </Button>
            )}

            <div className="w-px h-5 bg-white/10" />

            <Button variant="ghost" size="icon" className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10" onClick={onClose} data-testid="exit-presentation-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <div className="flex justify-between text-[10px] text-white/30 mt-1">
          <span>{snapshots?.[0]?.periodLabel}</span>
          <span>Step {currentIndex + 1} / {total}</span>
          <span>{snapshots?.[total - 1]?.periodLabel}</span>
        </div>
      </div>
    </div>
  );
}

function NavBtn({ icon: Icon, onClick }) {
  return (
    <button onClick={onClick} className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
      <Icon className="w-4 h-4" />
    </button>
  );
}
